/**
 * Um risco das orientações (E158.F3, GUS-191 CA11): a providência primeiro, o motivo, a resposta
 * disponível ou a falta dela, o grau com a razão — e a fonte e os detalhes por expansão, acessíveis
 * por teclado (`<details>`). O grau e a ordem vêm do servidor; aqui nada é reclassificado.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { GrauDoRisco, RiscoNaOrientacaoData } from '@/lib/orientacoes';

const t = ptBR.orientacoes;

const COR_DO_GRAU: Record<GrauDoRisco, string> = {
  alto: 'border-danger-500 bg-danger-50 text-danger-700',
  medio: 'border-warning-500 bg-warning-50 text-warning-700',
  baixo: 'border-border bg-surface text-fg-muted',
};

function Linha({ rotulo, texto }: { rotulo: string; texto: string }) {
  return (
    <p className="mt-sc-2 max-w-prose text-body-sm text-fg">
      <span className="font-semibold">{rotulo}:</span> {texto}
    </p>
  );
}

function Detalhe({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,12rem)_1fr] gap-sc-2 text-caption">
      <dt className="text-fg-muted">{rotulo}</dt>
      <dd className="text-fg">{valor}</dd>
    </div>
  );
}

export function RiscoDaOrientacao({ risco }: { risco: RiscoNaOrientacaoData }) {
  const titulo = `risco-${risco.numero}`;
  return (
    <article
      className="border-t border-border py-sc-4 first:border-t-0"
      aria-labelledby={titulo}
      data-testid={`risco-${risco.numero}`}
      data-grau={risco.grau}
    >
      <div className="flex flex-wrap items-start justify-between gap-sc-2">
        <h3 id={titulo} className="max-w-prose text-body-md font-semibold text-fg">
          {risco.providencia}
        </h3>
        <span className={`border px-sc-2 py-0.5 text-caption ${COR_DO_GRAU[risco.grau]}`}>
          {t.risco.grau[risco.grau]}
        </span>
      </div>
      <Linha rotulo={t.risco.motivo} texto={risco.motivo} />
      <Linha rotulo={t.risco.resposta} texto={risco.resposta ?? t.risco.semResposta} />
      <Linha rotulo={t.risco.razao} texto={risco.razao_do_grau} />
      <p className="mt-sc-2 text-caption text-fg-muted">
        {risco.tambem_no_laudo ? t.risco.tambemNoLaudo : t.risco.soNesteRelatorio}
      </p>
      <details className="mt-sc-2">
        <summary className="cursor-pointer text-body-sm text-accent-700">
          {t.risco.detalhes}
        </summary>
        <dl className="mt-sc-2 space-y-1">
          <Detalhe rotulo={t.risco.fonte} valor={risco.fonte} />
          <Detalhe rotulo={t.risco.fundamento} valor={risco.fundamento_atingido} />
          <Detalhe rotulo={t.risco.usoContrario} valor={risco.uso_pela_parte_contraria} />
          <Detalhe rotulo={t.risco.natureza} valor={t.natureza[risco.natureza]} />
          <Detalhe rotulo={t.risco.responsavel} valor={risco.responsavel} />
          <Detalhe rotulo={t.risco.estado} valor={t.estadoDoRisco[risco.estado]} />
          <Detalhe rotulo={t.risco.identificador} valor={`R${risco.numero} · ${risco.id}`} />
        </dl>
      </details>
    </article>
  );
}
